import axios from 'axios'
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { getCurrentLanguage } from '@hgj/common-tools'

import { SECOND } from '@/plugins/constant'
import { createHeader } from './common'
import { ElMessage } from '@hgj/element-plus'
import router from '@/plugins/router'
import { openDialog } from '@hgj/storybrooke-business-tools'
import AgreementDialogVue from '@/components/dialog/AgreementDialog.vue'
import { useIframe } from '@/hooks/useIframe'

// 请求超时时间（秒）
const MAX_TIME_OUT = 30
// 取消请求标记
export const source = axios.CancelToken.source()

// 创建axios实例
const request = axios.create({
  baseURL: import.meta.env.VITE_API_HOST,
  timeout: MAX_TIME_OUT * SECOND,
})

/**
 * @description: 请求前对请求参数处理
 * @param {InternalAxiosRequestConfig} config 请求参数
 * @return {InternalAxiosRequestConfig}
 */
const requestInterceptors = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  config.cancelToken = source.token
  // 配置headers
  createHeader(config)
  const iframe = useIframe()
  if (iframe.isInIframe && !iframe.isFromAiAdmin) {
    await iframe.waitForIframeProps()
    createHeader(config)
  }
  const lang = getCurrentLanguage() || 'zh-CN'
  config.headers['mixmicro-service-feign-invoke'] = true
  config.headers['X-Lang'] = lang
  config.headers['X-Zone-Offset'] = 28800
  config.headers['Language'] = lang
  return config
}

const agreementDialogInsets = ref(0)
/**
 * @description: 响应后对响应值处理
 * @param {AxiosResponse} res http响应体
 * @return {AxiosResponse}
 */
const responseInterceptors = (res: AxiosResponse): AxiosResponse => {
  if (res.config.responseType === 'blob') {
    return res
  }
  if (res.status === 200 && !res.data.code) {
    return res
  }

  const { code, message } = res.data
  if ([520000530, 520000402].includes(code)) {
    ElMessage.error(message)
    return res
  }
  if (code === 411) {
    // 未同意协议
    if (!agreementDialogInsets.value) {
      agreementDialogInsets.value++
      openDialog(AgreementDialogVue).then((value) => {
        agreementDialogInsets.value = 0
        value && router.push('/')
      })
    }
  }

  // 落地页未登录(401)、未授权协议(411)不报错，其余报错
  if (
    (router.currentRoute.value.name as string)?.includes('LandingPage') ||
    typeof router.currentRoute.value?.name === 'undefined'
  ) {
    if (![401, 411, 200].includes(code)) {
      ElMessage.error(message)
    }
    return res
  }

  // 错误码处理
  // 未登录和未开通应用
  if ([401, 410].includes(code)) {
    router.push({ name: 'LandingPage' })
    return res
  }

  if (
    ['LandingPage', undefined].includes(
      router.currentRoute.value.name as string,
    ) &&
    code === 200
  ) {
    router.push({ name: '/' })
  }
  return res
}

/**
 * @description: 错误处理
 * @param {any} error 错误信息
 * @return {Promise}
 */
const errorHandler = <T>(error: T): Promise<T> => {
  console.error('error request:', error)
  return Promise.reject((error as any)?.response?.data)
}
// 请求拦截器
request.interceptors.request.use(requestInterceptors, errorHandler)

// 响应拦截器
request.interceptors.response.use(responseInterceptors, errorHandler)

export default request
